import { createSlice } from '@reduxjs/toolkit';

import { fromApiContentMapper } from '../content.mapper';
import type { ContentState } from '../content.types';
import { extractContentApiError } from '../content.utils';
import { contentAdapter } from './content.selectors';
import {
  archiveContent,
  fetchContentById,
  manageContentTranslationValidation,
  unarchiveContent,
  updateContent,
} from './content.thunks';

export const contentState: ContentState = contentAdapter.getInitialState({
  metadata: {
    total: 0,
    loading: false,
    error: null,
    isFiltering: false,
  },
});

export const { actions: contentActions, reducer: contentReducer } = createSlice({
  name: 'contents',
  initialState: contentState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchContentById
    builder.addCase(fetchContentById.pending, (state) => {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          loading: true,
          error: null,
        },
      };
    });
    builder.addCase(fetchContentById.fulfilled, (state, { payload }) => {
      const newState = {
        ...state,
        metadata: {
          ...state.metadata,
          loading: false,
          error: null,
        },
      };
      return contentAdapter.upsertOne(newState, fromApiContentMapper(payload));
    });

    // updateContent
    builder.addCase(updateContent.pending, (state) => {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          loading: true,
          error: null,
        },
      };
    });
    builder.addCase(updateContent.rejected, (state, { payload }) => {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          error: extractContentApiError(payload),
          loading: false,
        },
      };
    });
    builder.addCase(updateContent.fulfilled, (state, { payload }) => {
      const newState = {
        ...state,
        metadata: {
          ...state.metadata,
          loading: false,
          error: null,
        },
      };
      return contentAdapter.upsertOne(newState, fromApiContentMapper(payload));
    });

    // manageContentTranslationValidation
    builder.addCase(manageContentTranslationValidation.pending, (state) => {
      state.metadata.loading = true;
      state.metadata.error = null;

      return state;
    });
    builder.addCase(manageContentTranslationValidation.rejected, (state, { payload }) => {
      state.metadata.loading = false;
      state.metadata.error = extractContentApiError(payload);

      return state;
    });
    builder.addCase(manageContentTranslationValidation.fulfilled, (state, { payload }) => {
      state.metadata.loading = false;
      state.metadata.error = null;

      return contentAdapter.upsertOne(state, fromApiContentMapper(payload));
    });

    // archiveContent
    builder.addCase(archiveContent.pending, (state) => {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          loading: true,
          error: null,
        },
      };
    });
    builder.addCase(archiveContent.rejected, (state, { payload }) => {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          error: extractContentApiError(payload),
          loading: false,
        },
      };
    });
    builder.addCase(archiveContent.fulfilled, (state, { payload }) => {
      const newState = {
        ...state,
        metadata: {
          ...state.metadata,
          loading: false,
          error: null,
        },
      };
      return contentAdapter.upsertOne(newState, fromApiContentMapper(payload));
    });

    // unarchiveContent
    builder.addCase(unarchiveContent.pending, (state) => {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          loading: true,
          error: null,
        },
      };
    });
    builder.addCase(unarchiveContent.rejected, (state, { payload }) => {
      return {
        ...state,
        metadata: {
          ...state.metadata,
          error: extractContentApiError(payload),
          loading: false,
        },
      };
    });
    builder.addCase(unarchiveContent.fulfilled, (state, { payload }) => {
      const newState = {
        ...state,
        metadata: {
          ...state.metadata,
          loading: false,
          error: null,
        },
      };
      return contentAdapter.upsertOne(newState, fromApiContentMapper(payload));
    });
  },
});
